import * as React from 'react';
import { useRef, MutableRefObject, useEffect } from 'react';
import MediaObject from 'components/MediaObject';
import Text from 'components/Text';
import Menu from 'components/Menu';
import MenuItem from 'components/MenuItem';
import MenuDivider from 'components/MenuDivider';
import LinkConstants from 'constants/LinkConstants';
import TextWithIcon from 'components/TextWithIcon';
import Icon from 'components/Icon';

import { Intent } from 'translations/types';
import 'translations/components/styles/TranslationPreview.scss';

type Props = {
  activeIntent: Intent;
  menuOpen: boolean;
  toggleMenuOpen: () => void;
  selectIntent: (newIntent: Intent) => void;
};

const intentMenuString = {
  Edited: 'Edit',
  Autotranslated: 'Autotranslate from original',
  Off: 'Turn this translation off',
};

function TranslationPreviewIntentMenu({
  activeIntent,
  menuOpen,
  toggleMenuOpen,
  selectIntent,
}: Props) {
  const componentRef = useRef<HTMLDivElement>() as MutableRefObject<HTMLDivElement>;
  const intents: Intent[] = ['Edited', 'Autotranslated', 'Off'];

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [menuOpen]);

  const handleClickOutside = (event: Event) => {
    if (menuOpen && componentRef.current && !componentRef.current.contains(event.target as Node)) {
      toggleMenuOpen();
    }
  };

  const getMenuItem = (opt: Intent) => {
    return (
      <MenuItem
        key={opt}
        menuKey={opt}
        onSelect={() => {
          selectIntent(opt);
        }}
        data-test-selector={opt + '-menu-item'}>
        <MediaObject figure="left" gutter="medium" className="TranslationPreview-intent-menu-item">
          <Icon name={activeIntent === opt ? 'check' : null} size="medium" color={'ink'} />
          <Text align="left" size="xsmall" color="black" lineHeight="flush">
            {intentMenuString[opt]}
          </Text>
        </MediaObject>
      </MenuItem>
    );
  };

  return (
    <div
      className="TranslationPreview-intent-menu"
      data-test-selector="translation-preview-intent-menu-button"
      ref={componentRef}
      onClick={toggleMenuOpen}>
      <TextWithIcon
        data-test-selector="translation-preview-menu"
        icon="chevron-down"
        iconSize="small"
        appendIcon
        center
        gutter="xxsmall"
        lineHeight="xlarge"
        weight="semibold">
        {activeIntent + ' '}
      </TextWithIcon>
      {menuOpen && (
        <Menu rowHover>
          {intents.map(opt => getMenuItem(opt))}
          <MenuDivider/>
          <MenuItem href={LinkConstants.TRANSLATIONS_TOOLTIP_HELP} target="_blank">
            <MediaObject figure="left" gutter="medium">
              <Icon name={null} size="medium" />
              <Text align="left" size="xsmall" color="black" lineHeight="flush">
                <Text color="mascot">Learn more</Text>
              </Text>
            </MediaObject>
          </MenuItem>
        </Menu>
      )}
    </div>
  );
}

export { TranslationPreviewIntentMenu, Props };
